import React, { useState } from 'react';
import ConsignedCard from './ConsignedCard';
import styled from 'styled-components';
import BasicPagination from 'src/common/refactoredComponents/paginator';
import BottomNavigation from 'src/common/refactoredComponents/bottomNavigation/BottomNavigation';
import Select from 'src/common/refactoredComponents/select/Select';
import { useTranslation } from 'react-i18next';
import SortAscending from 'src/assets/images/SortAscending.svg';
import SortDescending from 'src/assets/images/SortDescending.svg';
import { DARK_GRAY_FONT_COLOR } from 'src/styles/fonts/MCFontConstants';

const MobileViewWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 90%;
`;

const SortContainer = styled.div`
    position: sticky;
    top: 0;
    display: flex;
    align-items: center;
    margin: 8px 20px 8px 15px;
    z-index: 10;
    background: #f5f5f5;
`;

const MobileViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0 0 5px 0;
    margin: 0;
    min-height: 70vh;
    gap: 8px;
    overflow-y: auto;
    flex: 1;
`;

const CardWrapper = styled.div`
    flex-shrink: 0;
    width: 100%;
`;

const PaginationContainerMobile = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    padding: ${({ padding }) => padding ? padding : '0px'};
`;

const ConsignedMobileView = ({
    data = [],
    totalRecords = 0,
    first = 0,
    rows = 25,
    paginator = true,
    onPageChange,
    onSortChange,
    paginationInfoText = 'Showing Products',
}) => {
    const { t } = useTranslation();
    const page = Math.floor(first / rows);
    const [selectedSort, setSelectedSort] = useState('account_asc');

    // Sort options for the dropdown
    const sortOptions = [
        { 
            sortId: 'account_asc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.accountAscending'), 
            sortCode: 'partnerLocName', 
            sortOrder: 'ASC' 
        },
        { 
            sortId: 'account_desc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.accountDescending'), 
            sortCode: 'partnerLocName', 
            sortOrder: 'DESC' 
        },
        { 
            sortId: 'cfn_asc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.cfnAZ'), 
            sortCode: 'cfn', 
            sortOrder: 'ASC' 
        },
        { 
            sortId: 'cfn_desc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.cfnZA'), 
            sortCode: 'cfn', 
            sortOrder: 'DESC' 
        },
        { 
            sortId: 'description_asc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.descriptionAZ'), 
            sortCode: 'productDescription', 
            sortOrder: 'ASC' 
        },
        { 
            sortId: 'description_desc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.descriptionZA'), 
            sortCode: 'productDescription', 
            sortOrder: 'DESC' 
        },
        { 
            sortId: 'lot/serial_asc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.lotSerialAZ'), 
            sortCode: 'batch', 
            sortOrder: 'ASC' 
        },
        { 
            sortId: 'lot/serial_desc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.lotSerialZA'), 
            sortCode: 'batch', 
            sortOrder: 'DESC' 
        },
        { 
            sortId: 'onHand_asc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.onHandAscending'), 
            sortCode: 'stock', 
            sortOrder: 'ASC' 
        },
        { 
            sortId: 'onHand_desc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.onHandDescending'), 
            sortCode: 'stock', 
            sortOrder: 'DESC' 
        },
        { 
            sortId: 'useByDate_asc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.useByDateOldest'), 
            sortCode: 'sled', 
            sortOrder: 'ASC' 
        },
        { 
            sortId: 'useByDate_desc', 
            sortName: t('receiveSets.InventoryConsigned.sortOptions.useByDateNewest'), 
            sortCode: 'sled', 
            sortOrder: 'DESC' 
        },
    ];

    const sortIconSelection = (sortId) => {
        if (sortId?.endsWith('_asc')) {
            return SortAscending;
        }

        return SortDescending;
    };

    const sortIconSelectionValue = sortIconSelection(selectedSort);

    const handleSortChange = (sortId) => {
        setSelectedSort(sortId);
        if (onSortChange) {
            const selectedOption = sortOptions.find(option => option.sortId === sortId);
            if (selectedOption) {
                onSortChange({
                    sortCode: selectedOption.sortCode,
                    sortOrder: selectedOption.sortOrder
                });
            }
        }
    };

    return (
        <MobileViewWrapper>
            <SortContainer>
                <Select
                    id={"consigned-mobile-sort"}
                    value={selectedSort}
                    options={sortOptions}
                    optionLabel="sortName"
                    optionValue="sortId"
                    onChange={handleSortChange}
                    height={'40px'}
                    width={'100%'}
                    placeholder={t('receiveSets.InventoryConsigned.sortPlaceholder')}
                    scrollHeight={'365px'}
                    selectedLabelFontSize={'14px'}
                    labelColor={DARK_GRAY_FONT_COLOR}
                    borderRadius={'6px'}
                    sortIcon={sortIconSelectionValue}
                    isFromInventoryConsignment={true}
                    hideClear={true}
                />
            </SortContainer>
            <MobileViewContainer>
                {data.length !== 0 && (
                    data.map((item, index) => (
                        <CardWrapper key={`consigned-wrapper-${index}`}>
                            <ConsignedCard 
                                key={`consigned-${index}`} 
                                data={item} 
                                index={index}
                            />
                        </CardWrapper>
                    ))
                )}
            </MobileViewContainer>
            
            {paginator && totalRecords > 0 && (
                <BottomNavigation
                    padding={'0px'}
                    mbPadding={'0px'}
                    mbPosition={'fixed'}
                    zIndex={1}
                >
                    <PaginationContainerMobile padding={'0px'}>
                        <BasicPagination
                            totalRecords={totalRecords}
                            recordsPerPage={rows}
                            recordsFetched={data?.length > 0 ? data.length : 0}
                            pageNumber={page}
                            handleChange={(e) => onPageChange(e)}
                            first={first}
                            mobilePadding={'2% 2% 2% 2%'}
                            content={paginationInfoText}
                        />
                    </PaginationContainerMobile>
                </BottomNavigation>
            )}
        </MobileViewWrapper>
    );
};

export default ConsignedMobileView;
